import { FileIcon, FolderIcon, RotateCcw, Trash2 } from 'lucide-react';
import type { WorkGroupTrashFileEntry, WorkGroupTrashFolderEntry } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import {
  usePermanentlyDeleteTrashFile,
  usePermanentlyDeleteTrashFolder,
  useRestoreTrashFile,
  useRestoreTrashFolder,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';

interface WorkGroupTrashListProps {
  workGroupId: string;
  folders: WorkGroupTrashFolderEntry[];
  files: WorkGroupTrashFileEntry[];
  privateAccessReason?: string;
}

export function WorkGroupTrashList({
  workGroupId,
  folders,
  files,
  privateAccessReason,
}: WorkGroupTrashListProps) {
  const { hasAnyCapability } = usePermissions();
  const canRestore = hasAnyCapability([
    'workgroups.trash.restore.own',
    'workgroups.trash.restore.any',
  ]);
  const canDelete = hasAnyCapability([
    'workgroups.trash.delete.own',
    'workgroups.trash.delete.any',
  ]);

  const restoreFile = useRestoreTrashFile();
  const deleteFile = usePermanentlyDeleteTrashFile();
  const restoreFolder = useRestoreTrashFolder();
  const deleteFolder = usePermanentlyDeleteTrashFolder();

  const onError = (err: unknown) => {
    toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
  };

  return (
    <div className="space-y-6">
      {folders.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-foreground">Folders</h2>
          <ul className="space-y-1">
            {folders.map((folder) => (
              <li
                key={folder.id}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground"
              >
                <FolderIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                {canRestore ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    aria-label={`Restore ${folder.name}`}
                    disabled={restoreFolder.isPending}
                    onClick={() =>
                      restoreFolder.mutate(
                        { workGroupId, folderId: folder.id, privateAccessReason },
                        { onError },
                      )
                    }
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
                {canDelete ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-destructive"
                    aria-label={`Permanently delete ${folder.name}`}
                    disabled={deleteFolder.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Permanently delete folder "${folder.name}" and its contents? This frees storage immediately and cannot be undone.`,
                        )
                      ) {
                        deleteFolder.mutate(
                          { workGroupId, folderId: folder.id, privateAccessReason },
                          { onError },
                        );
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {files.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-foreground">Files</h2>
          <ul className="space-y-1">
            {files.map((file) => (
              <li
                key={file.id}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground"
              >
                <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                {canRestore ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    aria-label={`Restore ${file.name}`}
                    disabled={restoreFile.isPending}
                    onClick={() =>
                      restoreFile.mutate(
                        {
                          workGroupId,
                          fileId: file.id,
                          folderId: file.folderId,
                          privateAccessReason,
                        },
                        { onError },
                      )
                    }
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
                {canDelete ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-destructive"
                    aria-label={`Permanently delete ${file.name}`}
                    disabled={deleteFile.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Permanently delete "${file.name}"? This frees storage immediately and cannot be undone.`,
                        )
                      ) {
                        deleteFile.mutate(
                          { workGroupId, fileId: file.id, privateAccessReason },
                          { onError },
                        );
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
